import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/ui-kit/PageBits';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { User as UserIcon, KeyRound, Save } from 'lucide-react';

export default function Profile() {
  const { user, refresh } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    api.get('/auth/me').then(r => {
      setFirstName(r.data.user?.first_name || '');
      setLastName(r.data.user?.last_name || '');
    });
  }, []);

  const saveName = async () => {
    if (!firstName.trim() || !lastName.trim()) { toast.error('Nombre y apellido son obligatorios'); return; }
    setSavingName(true);
    try {
      await api.patch('/auth/me', { first_name: firstName.trim(), last_name: lastName.trim() });
      await refresh();
      toast.success('Datos actualizados');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSavingName(false); }
  };

  const savePwd = async () => {
    if (!pwd.current || !pwd.next) { toast.error('Rellena todos los campos'); return; }
    if (pwd.next.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    if (pwd.next !== pwd.confirm) { toast.error('Las contraseñas no coinciden'); return; }
    setSavingPwd(true);
    try {
      const { data } = await api.post('/auth/change-password', {
        current_password: pwd.current, new_password: pwd.next
      });
      if (data?.token) localStorage.setItem('c4k_token', data.token);
      toast.success('Contraseña actualizada.');
      setPwd({ current: '', next: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setSavingPwd(false); }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <PageHeader title="Mi perfil" subtitle={user?.email} testId="profile-header"/>

      <div className="rounded-3xl bg-white border border-slate-200 p-8 mb-6 flex items-center gap-6">
        <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-indigo-500 to-blue-600 grid place-items-center text-white text-2xl font-display font-extrabold">
          {user?.first_name?.[0]}
        </div>
        <div>
          <div className="font-display text-2xl font-extrabold" data-testid="profile-display-name">{user?.first_name} {user?.last_name}</div>
          <div className="text-sm text-slate-500 capitalize">{(user?.role||'').replace('_',' ')}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-700 grid place-items-center"><UserIcon className="w-4 h-4"/></div>
            <h2 className="font-display font-bold text-xl">Cambiar nombre</h2>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="fn">Nombre</Label>
              <Input id="fn" data-testid="profile-first-name" value={firstName} onChange={(e)=>setFirstName(e.target.value)}/>
            </div>
            <div>
              <Label htmlFor="ln">Apellido</Label>
              <Input id="ln" data-testid="profile-last-name" value={lastName} onChange={(e)=>setLastName(e.target.value)}/>
            </div>
            <Button data-testid="profile-save-name-btn" onClick={saveName} disabled={savingName}
              className="rounded-full bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-1"/> {savingName ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl bg-rose-100 text-rose-700 grid place-items-center"><KeyRound className="w-4 h-4"/></div>
            <h2 className="font-display font-bold text-xl">Cambiar contraseña</h2>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="cp">Contraseña actual</Label>
              <Input id="cp" data-testid="profile-current-pwd" type="password" value={pwd.current}
                onChange={(e)=>setPwd({...pwd, current: e.target.value})}/>
            </div>
            <div>
              <Label htmlFor="np">Nueva contraseña</Label>
              <Input id="np" data-testid="profile-new-pwd" type="password" value={pwd.next}
                onChange={(e)=>setPwd({...pwd, next: e.target.value})}/>
            </div>
            <div>
              <Label htmlFor="cf">Confirmar</Label>
              <Input id="cf" data-testid="profile-confirm-pwd" type="password" value={pwd.confirm}
                onChange={(e)=>setPwd({...pwd, confirm: e.target.value})}/>
            </div>
            <Button data-testid="profile-save-pwd-btn" onClick={savePwd} disabled={savingPwd}
              className="rounded-full bg-rose-600 hover:bg-rose-700">
              <KeyRound className="w-4 h-4 mr-1"/> {savingPwd ? 'Guardando…' : 'Actualizar contraseña'}
            </Button>
            <p className="text-xs text-slate-500">Al cambiarla, se cerrarán tus sesiones en otros dispositivos.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
